import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Star, Play, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatDuration, thumbUrl } from '../lib/api';

const ContentPreviewModal = ({ film, allFilms, onClose, onCreatorClick }) => {
  const [current, setCurrent] = useState(film);
  const [history, setHistory] = useState([film]);
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const handleSelectRelated = (f) => {
    setHistory(prev => [...prev, f]);
    setCurrent(f);
  };

  const handleWatchNow = () => {
    onClose();
    if (isAuthenticated) {
      navigate(`/watch/${current.film_id}`);
    } else {
      navigate('/login', { state: { from: `/watch/${current.film_id}` } });
    }
  };

  const others = allFilms.filter(f => f.film_id !== current.film_id);

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 260, behavior: 'smooth' });
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}
      data-testid="content-preview-modal">
      <div className="bg-[#0F0B1E] border border-[#8B5CF6]/20 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        {/* Hero image */}
        <div className="relative h-56 md:h-64">
          <img src={thumbUrl(current.thumbnail, 640)} alt={current.title}
            className="w-full h-full object-cover rounded-t-2xl" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F0B1E] via-transparent to-transparent" />
          <button onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center hover:bg-black/80"
            data-testid="preview-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 -mt-12 relative">
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            data-testid="preview-title">
            {current.title}
          </h2>
          <div className="flex items-center gap-3 mb-3 text-sm">
            <span className="flex items-center gap-1 text-[#00FF9D] font-semibold">
              <Star className="w-4 h-4 fill-[#00FF9D]" /> {current.avg_rating?.toFixed(1) || '0.0'}
            </span>
            {current.year && <span className="text-gray-400">{current.year}</span>}
            {current.duration ? <span className="text-gray-400">{formatDuration(current.duration)}</span> : null}
            {current.content_type && (
              <span className="badge text-xs">{current.content_type === 'series' ? 'Series' : 'Film'}</span>
            )}
          </div>
          {current.creator && (
            <p className="text-xs text-gray-500 mb-2">
              by{' '}
              <button onClick={() => onCreatorClick?.(current.creator)}
                className="text-[#A78BFA] hover:text-[#8B5CF6] hover:underline transition-colors"
                data-testid="preview-creator-link">
                {current.creator}
              </button>
            </p>
          )}
          {current.genres?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {current.genres.map(g => (
                <span key={g} className="text-[10px] px-2 py-0.5 rounded-full bg-[#8B5CF6]/10 text-[#8B5CF6] border border-[#8B5CF6]/20">
                  {g}
                </span>
              ))}
            </div>
          )}
          <p className="text-gray-400 text-sm leading-relaxed mb-5" data-testid="preview-description">
            {current.description || 'No description available.'}
          </p>

          {/* Watch Now button */}
          <button onClick={handleWatchNow}
            className="w-full btn-white flex items-center justify-center gap-2 py-3 text-sm font-semibold mb-6"
            data-testid="preview-watch-btn">
            <Play className="w-5 h-5 fill-current" /> Watch Now
          </button>

          {/* Related content scroller */}
          {others.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-400 mb-3">More to explore</h3>
              <div className="relative">
                {others.length > 3 && (
                  <>
                    <button onClick={() => scroll(-1)}
                      className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-7 h-7 bg-black/70 rounded-full flex items-center justify-center hover:bg-black">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button onClick={() => scroll(1)}
                      className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-7 h-7 bg-black/70 rounded-full flex items-center justify-center hover:bg-black">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </>
                )}
                <div ref={scrollRef}
                  className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
                  {others.map(f => (
                    <button key={f.film_id}
                      onClick={() => handleSelectRelated(f)}
                      className="shrink-0 w-40 group text-left"
                      data-testid={`related-${f.film_id}`}>
                      <div className="relative rounded-lg overflow-hidden aspect-video mb-1.5">
                        <img src={thumbUrl(f.thumbnail, 240)} alt={f.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                          <Play className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                      <p className="text-xs font-medium truncate group-hover:text-[#8B5CF6] transition-colors">
                        {f.title}
                      </p>
                      <div className="flex items-center gap-1 text-[10px] text-gray-500">
                        <Star className="w-2.5 h-2.5 text-[#8B5CF6]" /> {f.avg_rating?.toFixed(1) || '0.0'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentPreviewModal;
